// ---------------------------------------------------------------------------
// Client half of "imported .fig → project design system".
//
// Calls the same-origin Pages Function POST /api/design-system/project-
// registration with the signed-in user's Supabase access token. Nothing about
// the design travels in the request: the server reads the accepted token
// decisions, the token values and the rights declaration off the project row
// the caller owns. See functions/api/design-system/project-registration.ts for
// why that direction is load-bearing rather than merely tidy.
//
// The `expected` pin is the one thing the caller contributes, and it can only
// NARROW: a stale tab that still shows an older import cannot register a source
// the user has since replaced — the server fails closed on mismatch.
// ---------------------------------------------------------------------------

import { useStudioStore } from '@/store/studio-store'

export const PROJECT_DESIGN_SYSTEM_REGISTRATION_ENDPOINT =
  '/api/design-system/project-registration' as const

/** Mirrors `functions/_lib/project-design-system-registration.ts`. */
export interface ProjectDesignSystemRegistrationSummary {
  schemaVersion: 'cogo.project-design-system-registration.v1'
  designSystemId: string
  projectId: string
  source: {
    sourceId: string
    sourceRevision: number
    workspaceRevision: number
    fileName: string
    fileSha256: string
    sourceArtifactRef: string
  }
  authority: {
    scope: 'project-only'
    previewGenerationAllowed: true
    productionGenerationAllowed: false
    packPromotionAllowed: false
    retrievalCorpusIngestionAllowed: false
    ontologyAdmissionAllowed: false
    redistributionAllowed: false
    modelMayAuthor: false
  }
  derivation: {
    authorityKind: 'server-derived-from-persisted-project-source'
    acceptedTokenCount: number
    projectedTokenCount: number
    unprojectedTokens: Array<{ tokenId: string; reason: string }>
  }
  designSystemTokens: Record<string, string>
  themeHint: { primaryColor: string; style: string }
  registeredAt: string
  contentHash: string
}

export type RegisterProjectDesignSystemOutcome =
  | { ok: true; registration: ProjectDesignSystemRegistrationSummary }
  | { ok: false; code: string; message: string }

const CLIENT_TIMEOUT_MS = 30_000

/**
 * User-legible wording for the fail-closed codes the server can return. The
 * server's own `message` is kept as the fallback so a new code never degrades
 * to a silent failure.
 */
const REASON_TEXT: Record<string, string> = {
  unauthorized: 'Sign in again to register this project’s design system.',
  project_access_denied: 'This project is not available to your account.',
  project_design_system_source_missing:
    'This project has no imported design source yet. Import a .fig first.',
  project_design_system_source_mismatch:
    'The project’s imported source changed. Reload the project and try again.',
  project_design_system_rights_missing:
    'Confirm the source rights declaration for this .fig before registering it.',
  project_design_system_rights_invalid:
    'The recorded rights declaration no longer verifies. Re-confirm it for this exact revision.',
  project_design_system_rights_scope_mismatch:
    'The rights declaration was confirmed for a different project or account.',
  project_design_system_rights_source_mismatch:
    'The rights declaration does not cover the source revision now in the project.',
  project_design_system_preview_authority_absent:
    'This source is quarantined for evaluation only and cannot drive generation.',
  project_design_system_no_accepted_tokens:
    'Accept at least one design token candidate first.',
  project_design_system_missing_render_floor:
    'Map accepted tokens to primary, background and foreground before registering — without all three the generated app would fall back to default colours.',
  project_design_system_readback_mismatch:
    'The registration did not persist. Nothing was changed; please try again.',
}

function authHeaders(): Record<string, string> | null {
  const session = useStudioStore.getState().session
  const token = session?.access_token?.trim()
  if (!token) return null
  const anonKey = (
    import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined
  )?.trim()
  return {
    Authorization: `Bearer ${token}`,
    ...(anonKey ? { apikey: anonKey } : {}),
  }
}

/**
 * Pure request core — exported for unit tests. `headers` and `fetchFn` are
 * injectable; the store-connected wrapper below supplies production wiring.
 */
export async function requestProjectDesignSystemRegistration(
  projectId: string,
  headers: Record<string, string>,
  expected?: { sourceId?: string; fileSha256?: string },
  fetchFn: typeof fetch = fetch,
): Promise<RegisterProjectDesignSystemOutcome> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS)
  try {
    const response = await fetchFn(
      PROJECT_DESIGN_SYSTEM_REGISTRATION_ENDPOINT,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          projectId,
          ...(expected?.sourceId || expected?.fileSha256
            ? { expected }
            : {}),
        }),
        signal: controller.signal,
      },
    )
    const body = (await response.json().catch(() => null)) as {
      registration?: ProjectDesignSystemRegistrationSummary
      code?: string
      message?: string
    } | null
    if (response.ok && body?.registration) {
      return { ok: true, registration: body.registration }
    }
    const code = body?.code ?? `http-${response.status}`
    return {
      ok: false,
      code,
      // The control that reaches here is labelled "Apply these to this
      // project", so the last-resort wording says that, names the status it
      // got, and states that nothing changed. It used to read "Registering the
      // project design system failed." — different vocabulary from the button,
      // no code, and no statement about what happened to the project. Observed
      // 2026-07-31 as the message for a bare HTTP 404.
      message:
        REASON_TEXT[code] ??
        body?.message ??
        `Could not apply these to this project (${code}). Nothing was changed.`,
    }
  } catch (error) {
    return {
      ok: false,
      code: (error as Error)?.name === 'AbortError' ? 'timeout' : 'transport',
      message: 'Could not reach the design-system registration service.',
    }
  } finally {
    clearTimeout(timeout)
  }
}

/**
 * Store-connected wrapper. Registers the CURRENT server project's accepted
 * .fig tokens as its project-scoped design system and pins the request to the
 * source the UI is actually showing.
 */
export async function registerCurrentProjectDesignSystem(): Promise<RegisterProjectDesignSystemOutcome> {
  const state = useStudioStore.getState()
  const projectId = state.currentProjectId
  if (!projectId || state.projectSource !== 'server') {
    return {
      ok: false,
      code: 'project_design_system_requires_server_project',
      message:
        'Open or save this as a server project before registering its design system.',
    }
  }
  const headers = authHeaders()
  if (!headers) {
    return {
      ok: false,
      code: 'unauthorized',
      message: REASON_TEXT.unauthorized!,
    }
  }
  const workspace = state.openPencilSourceWorkspace
  return requestProjectDesignSystemRegistration(projectId, headers, {
    sourceId: workspace?.sourceId,
    fileSha256: workspace?.bundle?.fileSha256 ?? undefined,
  })
}
